// Printable unit-completion certificate. CLAUDE.md §25 (parent-shareable) /
// §26.4 (Hijri alongside Gregorian).
//
// Deliberately an HTML page with print CSS rather than a server-generated PDF:
// PDF libraries don't do Arabic shaping or BiDi (pdf-lib #657), so Arabic would
// render as disconnected reversed letters. The browser shapes Arabic correctly
// for free — the parent hits Print and saves as PDF.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Button } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { useAuth } from '~/lib/auth';
import { listCertificates } from '~/lib/learn';
import { asLocale } from '~/lib/locale';

export const Route = createFileRoute('/$locale/certificate/$unitId')({
  component: CertificatePage,
});

function CertificatePage() {
  const t = useT();
  const navigate = useNavigate();
  const { locale: rawLocale, unitId } = useParams({ from: '/$locale/certificate/$unitId' });
  const locale = asLocale(rawLocale);
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['kids', 'certificates'],
    queryFn: ({ signal }) => listCertificates(signal),
  });

  const cert = query.data?.find((c) => c.unitId === unitId);

  if (query.isLoading) {
    return <p className="p-10 text-center text-ink-500">…</p>;
  }
  // Not earned (or not this learner's) — never render a certificate we can't back.
  if (!cert) {
    return (
      <div className="mx-auto max-w-md px-6 py-12 text-center">
        <p className="text-ink-700">{t('app.certificate.not_earned')}</p>
        <Button
          className="mt-5"
          onClick={() => void navigate({ to: '/$locale', params: { locale } })}
        >
          {t('content.player.back_home')}
        </Button>
      </div>
    );
  }

  const learnerName = user?.displayName ?? user?.username ?? '';
  const awarded = cert.completedAt ? new Date(cert.completedAt) : new Date();

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      {/* Screen-only controls — hidden when printing. */}
      <div className="mb-5 flex items-center justify-between print:hidden">
        <button
          type="button"
          onClick={() => void navigate({ to: '/$locale/badges', params: { locale } })}
          className="text-sm font-semibold text-burgundy-600 hover:underline"
        >
          ← {t('app.badges.title')}
        </button>
        <Button onClick={() => window.print()}>🖨️ {t('app.certificate.print')}</Button>
      </div>

      <CertificateSheet
        learnerName={learnerName}
        unitTitleKey={cert.unitTitleKey}
        trackTitleKey={cert.trackTitleKey}
        lessonCount={cert.lessonCount}
        awarded={awarded}
        locale={locale}
      />

      <p className="mt-5 text-center text-xs text-ink-500 print:hidden">
        {t('app.certificate.print_hint')}
      </p>
    </div>
  );
}

function CertificateSheet({
  learnerName,
  unitTitleKey,
  trackTitleKey,
  lessonCount,
  awarded,
  locale,
}: {
  learnerName: string;
  unitTitleKey: string;
  trackTitleKey: string;
  lessonCount: number;
  awarded: Date;
  locale: 'ar' | 'en';
}) {
  const t = useT();
  const { isRtl } = useLocale();

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className="relative aspect-[297/210] w-full overflow-hidden rounded-3xl bg-cream-50 p-[4%] shadow-[0_30px_80px_-40px_rgba(31,26,23,0.5)] ring-1 ring-burgundy-500/10 print:rounded-none print:shadow-none print:ring-0"
    >
      {/* Ornamental border */}
      <div className="absolute inset-[2.5%] rounded-2xl border-[3px] border-sun-300/70 print:rounded-none" />
      <div className="absolute inset-[3.5%] rounded-xl border border-burgundy-500/20 print:rounded-none" />

      <div className="relative flex h-full flex-col items-center justify-center px-[6%] text-center">
        <img src="/logo.png" alt="" className="h-[9%] min-h-8 w-auto object-contain" />

        <p className="mt-[2%] text-[1.6vw] font-bold uppercase tracking-[0.25em] text-sun-600 print:text-[10pt]">
          {t('app.certificate.heading')}
        </p>

        <p className="mt-[3%] text-[1.5vw] text-ink-700 print:text-[11pt]">
          {t('app.certificate.awarded_to')}
        </p>
        <p className="mt-[1%] font-display text-[4vw] font-bold leading-tight text-burgundy-700 print:text-[30pt]">
          <bdi>{learnerName}</bdi>
        </p>

        <p className="mt-[3%] max-w-[80%] text-[1.5vw] leading-relaxed text-ink-700 print:text-[11pt]">
          {t('app.certificate.body', { lessons: lessonCount })}
        </p>
        <p className="mt-[1.5%] text-[2.2vw] font-bold text-ink-900 print:text-[16pt]">
          <bdi>{t(unitTitleKey)}</bdi>
        </p>
        <p className="mt-[0.5%] text-[1.4vw] text-ink-500 print:text-[10pt]">
          <bdi>{t(trackTitleKey)}</bdi>
        </p>

        {/* Dates: Hijri alongside Gregorian (§26.4). */}
        <div className="mt-auto flex w-full items-end justify-between pt-[3%] text-[1.2vw] text-ink-500 print:text-[9pt]">
          <div className="text-start">
            <p className="font-semibold text-ink-700">{formatGregorian(awarded, locale)}</p>
            <p>{formatHijri(awarded, locale)}</p>
          </div>
          <div className="text-end">
            <p className="border-t border-ink-500/30 pt-1 font-semibold text-ink-700">
              {t('app.common.app_name')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatGregorian(d: Date, locale: 'ar' | 'en'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

// Umm al-Qura is the authoritative civil Hijri calendar in the Gulf. `Intl`
// ships it natively — no dependency, no lookup table to maintain.
function formatHijri(d: Date, locale: 'ar' | 'en'): string {
  try {
    return new Intl.DateTimeFormat(
      locale === 'ar' ? 'ar-SA-u-ca-islamic-umalqura' : 'en-GB-u-ca-islamic-umalqura',
      { day: 'numeric', month: 'long', year: 'numeric' },
    ).format(d);
  } catch {
    // Very old engines without the calendar — the Gregorian line still shows.
    return '';
  }
}
